import type { CSSProperties } from 'react';
import ticketImg from '../../icons/ticket/ticket.png';
import type { DeliveryUrgency } from '../../../../../../domain/economy/types';
import {
  getDeliveryPopupLayout,
  getDeliveryPopupSize,
  type DeliveryPopupTail,
} from './deliveryPopupLayout';
import {
  DeliveryPopupEmoji,
  shouldShowDeliveryPopupEmoji,
} from './DeliveryPopupEmoji';
import { deliveryPopupMoodId, getDeliveryPopupMoodAssets } from './deliveryPopupMood';
import { DeliveryPopupMessage } from './DeliveryPopupMessage';
import { DeliveryPopupReward } from './DeliveryPopupReward';
import { TakeDeliveryButton } from './TakeDeliveryButton';
import './DeliveryPopup.css';

export type DeliveryPopupProps = {
  urgency: DeliveryUrgency;
  waitingDay: number;
  passReward: number;
  /** `up` — tail-up chrome (Figma 190:9422). */
  tail?: DeliveryPopupTail;
  onTakeOrder?: () => void;
  className?: string;
  style?: CSSProperties;
};

/** Figma delivery popup — 1.2∶1 chrome; tail-down / tail-up layouts. */
export function DeliveryPopup({
  urgency,
  waitingDay,
  passReward,
  tail = 'down',
  onTakeOrder,
  className,
  style,
}: DeliveryPopupProps) {
  const moodId = deliveryPopupMoodId(urgency, waitingDay);
  const mood = getDeliveryPopupMoodAssets(urgency, waitingDay, tail);
  const size = getDeliveryPopupSize(tail);
  const layout = getDeliveryPopupLayout(tail);
  const ticket = layout.ticketIcon;
  const showEmoji = shouldShowDeliveryPopupEmoji(tail);

  return (
    <div
      className={['delivery-popup', className].filter(Boolean).join(' ')}
      style={{
        width: size.width,
        height: size.height,
        ...style,
      }}
    >
      <img
        className="delivery-popup__background"
        src={mood.backgroundUrl}
        alt=""
        draggable={false}
        aria-hidden
      />
      {showEmoji ? <DeliveryPopupEmoji moodId={moodId} tail={tail} /> : null}
      <DeliveryPopupReward passReward={passReward} layout={layout.rewardAmount} />
      <img
        className="delivery-popup__ticket-img"
        src={ticketImg}
        alt=""
        draggable={false}
        aria-hidden
        style={{
          left: ticket.left,
          top: ticket.top,
          width: ticket.width,
          height: ticket.height,
          transform: `rotate(${ticket.rotateDeg}deg)`,
        }}
      />
      <DeliveryPopupMessage urgency={urgency} waitingDay={waitingDay} tail={tail} />
      <TakeDeliveryButton layout={layout.takeDeliveryButton} onClick={onTakeOrder} />
    </div>
  );
}
